import { Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { ArrowRight, ShieldCheck, FileText, LineChart } from "lucide-react";

export default function Landing() {
  return (
    <div className="min-h-screen bg-white">
      <Navbar variant="landing" />

      {/* Hero */}
      <section className="relative overflow-hidden bg-[#0A2540] text-white">
        <div
          aria-hidden
          className="absolute inset-0 opacity-60"
          style={{
            background:
              "radial-gradient(1200px 500px at 10% 10%, rgba(180,151,90,0.18), transparent), radial-gradient(900px 400px at 90% 90%, rgba(255,255,255,0.06), transparent)",
          }}
        />
        <div
          aria-hidden
          className="absolute inset-0"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)",
            backgroundSize: "48px 48px",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-32 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 border border-white/20 rounded-sm px-3 py-1 text-[10px] uppercase tracking-[0.3em]">
              <span className="h-1.5 w-1.5 rounded-full bg-[#B4975A]" />
              Portal Institucional
            </div>
            <h1 className="mt-6 font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]">
              Gestione sus acciones
              <br />
              <span className="text-[#B4975A]">con total transparencia.</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-white/75 max-w-xl leading-relaxed">
              SmartNation es el portal oficial para accionistas: revise su cartera, descargue
              comunicados oficiales y contacte a nuestro equipo de relación con inversionistas
              desde un solo lugar.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-3">
              <Link to="/login" data-testid="hero-login-cta">
                <Button
                  size="lg"
                  className="bg-[#B4975A] hover:bg-[#9c814a] text-[#0A2540] font-semibold rounded-sm h-12 px-6"
                >
                  Ingresar a mi cuenta
                  <ArrowRight size={18} className="ml-2" />
                </Button>
              </Link>
            </div>
            <div className="mt-12 grid grid-cols-3 gap-6 max-w-xl">
              <Stat label="Transparencia" value="100%" />
              <Stat label="Acceso" value="24/7" />
              <Stat label="Cifrado" value="TLS" />
            </div>
          </div>
          <div className="lg:col-span-5">
            <div
              data-testid="roadmap-timeline"
              className="border border-white/15 rounded-sm p-8 bg-white/[0.03] backdrop-blur-sm"
            >
              <div className="flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-[0.3em] text-white/60">
                  Hoja de ruta
                </div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#B4975A] font-semibold">
                  2026 — 2028
                </div>
              </div>
              <h3 className="mt-3 font-heading text-2xl font-bold leading-snug">
                De la reorganización
                <br />
                <span className="text-white/70">a la escalabilidad global.</span>
              </h3>

              <ol className="mt-6 relative border-l border-white/15 ml-3 space-y-6">
                <TimelineItem
                  year="2026"
                  phase="Fase 01"
                  title="Reorganización y nuevos modelos de negocio"
                  desc="Cierre del proceso de reorganización de la compañía y evaluación comercial de nuevos servicios digitales como Ecomerce y Apps."
                  accent
                />
                <TimelineItem
                  year="2027"
                  phase="Fase 02"
                  title="Tracción Comercial"
                  desc="Despliegue de propuestas comerciales, alianzas estratégicas y primeros clientes clave para consolidar la oferta basada en IA."
                />
                <TimelineItem
                  year="2028"
                  phase="Fase 03"
                  title="Escalabilidad"
                  desc="Expansión operativa, crecimiento acelerado y escalamiento de los servicios a nuevos mercados y segmentos."
                />
              </ol>

              <div className="mt-6 text-xs text-white/50">
                Hitos estratégicos comunicados a nuestros accionistas.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
        <div className="max-w-2xl">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#475569]">Servicios</div>
          <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-semibold text-[#0A2540] tracking-tight">
            Todo lo que un accionista necesita
          </h2>
          <p className="mt-4 text-[#475569]">
            Tres módulos diseñados para que su experiencia como inversionista sea clara, simple y
            segura.
          </p>
        </div>

        <div className="mt-12 grid md:grid-cols-3 gap-6">
          <FeatureCard
            icon={<LineChart size={22} />}
            tag="01"
            title="Cartera de Acciones"
            desc="Visualice todas sus compras, cantidades y montos invertidos, con histórico detallado por contrato."
          />
          <FeatureCard
            icon={<FileText size={22} />}
            tag="02"
            title="Comunicados Oficiales"
            desc="Acceda a los documentos y comunicados emitidos por la compañía directamente en PDF."
          />
          <FeatureCard
            icon={<ShieldCheck size={22} />}
            tag="03"
            title="Solicitudes en línea"
            desc="Envíe consultas a nuestro equipo desde su perfil privado y reciba respuesta directa del administrador."
          />
        </div>
      </section>

      <footer className="border-t border-[#E2E8F0] py-10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 text-sm text-[#475569]">
          <div>© {new Date().getFullYear()} SmartNation S.A. Todos los derechos reservados.</div>
          <div className="flex items-center gap-6">
            <Link to="/login" className="hover:text-[#0A2540]">
              Acceso
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="font-heading text-2xl font-bold">{value}</div>
      <div className="text-[10px] uppercase tracking-[0.25em] text-white/60 mt-1">{label}</div>
    </div>
  );
}

function TimelineItem({ year, phase, title, desc, accent = false }) {
  return (
    <li data-testid={`timeline-${year}`} className="pl-6 relative">
      <span
        aria-hidden
        className={`absolute -left-[7px] top-1.5 h-3 w-3 rounded-full border-2 ${
          accent
            ? "bg-[#B4975A] border-[#B4975A]"
            : "bg-[#0A2540] border-white/40"
        }`}
      />
      <div className="flex items-center gap-3">
        <span
          className={`font-heading text-xl font-bold ${
            accent ? "text-[#B4975A]" : "text-white"
          }`}
        >
          {year}
        </span>
        <span className="text-[10px] uppercase tracking-[0.3em] text-white/50 border border-white/15 px-2 py-0.5 rounded-sm">
          {phase}
        </span>
      </div>
      <div className="mt-1.5 font-medium text-white">{title}</div>
      <p className="mt-1 text-xs text-white/60 leading-relaxed">{desc}</p>
    </li>
  );
}

function FeatureCard({ icon, tag, title, desc }) {
  return (
    <div
      data-testid={`feature-card-${tag}`}
      className="border border-[#E2E8F0] p-8 rounded-sm bg-white hover:-translate-y-0.5 transition-transform duration-150"
    >
      <div className="flex items-start justify-between">
        <div className="h-10 w-10 rounded-sm bg-[#0A2540] text-white flex items-center justify-center">
          {icon}
        </div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#B4975A] font-semibold">
          {tag}
        </div>
      </div>
      <h3 className="mt-6 font-heading text-xl font-semibold text-[#0A2540]">{title}</h3>
      <p className="mt-2 text-sm text-[#475569] leading-relaxed">{desc}</p>
    </div>
  );
}
